import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { BILLING } from "@/constants/testIds";
import { toast, Toaster } from "sonner";
import { Check, Sparkles } from "lucide-react";

export default function Billing() {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState({});

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setPlans(data.plans));
  }, []);

  const subscribe = async (lookup_key) => {
    setBusy((b) => ({ ...b, [lookup_key]: true }));
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy((b) => ({ ...b, [lookup_key]: false }));
    }
  };

  return (
    <div data-testid={BILLING.container} className="p-10">
      <Toaster position="top-right" richColors />
      <div className="mb-10 max-w-2xl">
        <div className="overline mb-3">Recurring revenue</div>
        <h1 className="font-display text-4xl font-black tracking-tighter mb-2">
          Activate an AI lead-gen plan
        </h1>
        <p className="text-neutral-500">
          After the free preview website, upsell each closed business into a
          monthly plan. Every plan runs AI agents that find, message and follow
          up on leads for them 24/7.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map((p, i) => {
          const featured = i === 1;
          return (
            <div
              key={p.lookup_key}
              data-testid={BILLING.planCard(p.lookup_key)}
              className={`border p-8 relative bg-white lf-fade-up ${
                featured ? "border-neutral-900" : "border-neutral-200"
              }`}
            >
              {featured && (
                <div className="absolute top-0 right-0 bg-black text-white px-3 py-1 text-[10px] tracking-widest uppercase font-bold">
                  Most sold
                </div>
              )}
              <div className="overline mb-2">Monthly</div>
              <h3 className="font-display text-3xl font-bold mb-1">
                {p.name}
              </h3>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="font-display text-5xl font-black tracking-tighter">
                  ${p.price}
                </span>
                <span className="text-neutral-500 text-sm">/mo</span>
              </div>
              <ul className="space-y-3 mb-8">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2 text-sm">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#00C781" }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={BILLING.planSubscribe(p.lookup_key)}
                onClick={() => subscribe(p.lookup_key)}
                disabled={busy[p.lookup_key]}
                className={
                  featured
                    ? "lf-btn-primary w-full flex items-center justify-center gap-2"
                    : "lf-btn-outline w-full flex items-center justify-center gap-2"
                }
              >
                <Sparkles className="w-4 h-4" />
                {busy[p.lookup_key] ? "Loading…" : "Subscribe"}
              </button>
            </div>
          );
        })}
      </div>
      <div className="mt-10 text-xs text-neutral-500 font-mono max-w-2xl">
        Test card: 4242 4242 4242 4242 · any future expiry · any CVC · any ZIP.
        Tax handling: Stripe manages tax + compliance (+3.5%/txn).
      </div>
    </div>
  );
}
